import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import useSize from '@react-hook/size';
import { Spin } from 'antd';

import { metIntegration } from '@metservicedev/platforms-services-interface';
import { WindDirectionSettings, IOneMinuteObservation, IWindDirectionData } from '@mono/types/src/weather-widgets/wind-direction-settings';
import { WeatherWidgetRenderProps } from '@mono/types/src/weather-widgets';
import { IssueTime } from '@mono/metui';
import { toHash } from '@mono/to-hash';
import { usePlatStageContext } from '@mono/use-plat-stage';
import { Dial } from '@mono/dial-component';
import { TextStyles } from '@mono/metui/src/utils/text-styles-selector';

export const WindDirection = (props: WeatherWidgetRenderProps<WindDirectionSettings>) => {
    const { settings, isLoading, theme: themeName } = props;
    const { stage } = usePlatStageContext();

    const isDarkMode = themeName === 'dark';
    const theme = isDarkMode
        ? {
              text: '#ffffff',
              issueTime: '#ffffff',
              bg: '#23262F',
          }
        : {
              text: '#172344',
              issueTime: '#595959',
              bg: '#ffffff',
          };

    const chartRef = useRef<HTMLDivElement>(null);
    const [width, height] = useSize(chartRef);

    const { title, timezone, displayTable, datasetId, mode } = settings;

    const { metserviceApi } = metIntegration;

    const targetTimezone = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

    const [data, setData] = useState<IWindDirectionData>({
        directionAverage1m: undefined,
        directionAverage10m: undefined,
        directionAverage1h: undefined,
        directionCwDevMax1m: undefined,
        directionCwDevMax10m: undefined,
        directionCwDevMax1h: undefined,
        directionCcwDevMax1m: undefined,
        directionCcwDevMax10m: undefined,
        directionCcwDevMax1h: undefined,
        issueTime: undefined,
    });

    useMemo(() => {
        if (!datasetId) return;
        const metjwt = localStorage.getItem('metjwt');

        let timeoutHook = 0;
        const abortController = new AbortController();

        const patientQuery = async () => {
            try {
                const headers: {} | { authorization: string } = metjwt
                    ? {
                          authorization: 'Bearer ' + metjwt,
                      }
                    : {};

                await fetch(`${metserviceApi[stage]}/observations/nz/1-minute/weatherStation/${datasetId?.split('.')[1]}/latest`, {
                    headers,
                    signal: abortController.signal,
                })
                    .then(async (res) => {
                        const value = await res.json();
                        console.log({ value });

                        const {
                            obs_timestamp,
                            winddir_01mnavg,
                            winddir_10mnavg,
                            winddir_01hravg,
                            windcw__01mnmax,
                            windcw__10mnmax,
                            windcw__01hrmax,
                            windccw_01mnmax,
                            windccw_10mnmax,
                            windccw_01hrmax,
                        } = value.results[0] as IOneMinuteObservation;

                        setData({
                            directionAverage1m: winddir_01mnavg,
                            directionAverage10m: winddir_10mnavg,
                            directionAverage1h: winddir_01hravg,
                            directionCwDevMax1m: windcw__01mnmax,
                            directionCwDevMax10m: windcw__10mnmax,
                            directionCwDevMax1h: windcw__01hrmax,
                            directionCcwDevMax1m: windccw_01mnmax,
                            directionCcwDevMax10m: windccw_10mnmax,
                            directionCcwDevMax1h: windccw_01hrmax,
                            issueTime: obs_timestamp,
                        });
                    })
                    .catch((e) => {
                        console.warn(e);
                        if (abortController.signal.aborted === true) return;
                    });
            } catch (e) {
                console.error(e);
            } finally {
                timeoutHook = setTimeout(patientQuery, 55 * 1000) as unknown as number;
            }
        };

        patientQuery();

        return () => {
            abortController.abort();
            clearTimeout(timeoutHook);
        };
    }, [toHash(datasetId), stage, metserviceApi]);

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                color: theme.text,
            }}
            ref={chartRef}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        color: theme.issueTime,
                    }}
                >
                    <div style={{ fontSize: 16, lineHeight: '22px', fontWeight: 700 }}>{title}</div>
                </div>
                <div style={{ display: 'flex', columnGap: 4 }}>
                    <IssueTime
                        issueTime={data.issueTime}
                        targetTimezone={targetTimezone}
                        isDarkMode={isDarkMode}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            color: theme.issueTime,
                        }}
                    />
                </div>
            </div>
            <Hr style={{ marginTop: 8 }} />
            {isLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
                        <Spin style={{ marginTop: 12 }} size='large' />
                    </div>
                </div>
            )}
            {!isLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Dial
                            arrowValue={data.directionAverage1m}
                            dashValue={data.directionAverage10m}
                            clockWiseDeviation={data.directionCwDevMax10m}
                            counterClockWiseDeviation={data.directionCcwDevMax10m}
                            height={displayTable ? height - 220 : height - 8}
                            width={width}
                            mode={mode}
                            theme={{ ...theme, themeName }}
                        />
                    </div>
                </div>
            )}
            {displayTable && !isLoading && (
                <div>
                    <div style={{ display: 'flex' }}>
                        <HeaderCell mode={settings.mode}></HeaderCell>
                        <HeaderCell mode={settings.mode}>1MIN</HeaderCell>
                        <HeaderCell mode={settings.mode}>10MIN</HeaderCell>
                        <HeaderCell mode={settings.mode}>60MIN</HeaderCell>
                    </div>
                    <Hr />
                    <div style={{ display: 'flex' }}>
                        <HeaderCell mode={settings.mode}>AVG</HeaderCell>
                        <TableCell mode={settings.mode}>{data.directionAverage1m}</TableCell>
                        <TableCell mode={settings.mode}>{data.directionAverage10m}</TableCell>
                        <TableCell mode={settings.mode}>{data.directionAverage1h}</TableCell>
                    </div>
                    <Hr />
                    <div style={{ display: 'flex' }}>
                        <HeaderCell mode={settings.mode}>CW</HeaderCell>
                        <TableCell mode={settings.mode}>{data.directionCwDevMax1m}</TableCell>
                        <TableCell mode={settings.mode}>{data.directionCwDevMax10m}</TableCell>
                        <TableCell mode={settings.mode}>{data.directionCwDevMax1h}</TableCell>
                    </div>
                    <Hr />
                    <div style={{ display: 'flex' }}>
                        <HeaderCell mode={settings.mode}>CCW</HeaderCell>
                        <TableCell mode={settings.mode}>{data.directionCcwDevMax1m}</TableCell>
                        <TableCell mode={settings.mode}>{data.directionCcwDevMax10m}</TableCell>
                        <TableCell mode={settings.mode}>{data.directionCcwDevMax1h}</TableCell>
                    </div>
                </div>
            )}
        </div>
    );
};

const HeaderCell = styled.div<{ mode?: 'cozy' | 'compact' }>`
    font-weight: 700;
    font-size: 12px;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 12px 0;
    white-space: nowrap;
    width: 100px;
    text-align: end;
    ${({ mode }) => TextStyles[mode ?? 'cozy'].h4}
    &:first-child {
        text-align: start;
    }
    &:last-child {
        border-bottom: none;
    }
`;

const TableCell = styled.div<{ mode?: 'cozy' | 'compact' }>`
    font-weight: 400;
    font-size: 14px;
    padding: 12px 0px 12px 0px;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    white-space: nowrap;
    width: fit-content;
    text-align: end;
    ${({ mode }) => TextStyles[mode ?? 'cozy'].p}
    &:first-child {
        text-align: start;
        font-weight: 700;
        font-size: 12px;
    }
    &:last-child {
        border-bottom: none;
    }
`;

const Hr = styled.hr`
    border: none;
    border-bottom: 1px solid #cdcdcd;
    margin: 0px;
`;
